import React, { useCallback, useContext, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import DotsIcon from '@shared/assets/icons/v2/dots.svg';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconButton } from '@shared/components/button';
import { MBHover } from '@shared/components/hover';
import { ShareSheet } from '@shared/scenes/cards/details/containers/ShareSheet';
import { useOpenCardOptionsMenu } from '@shared/scenes/cards/details/useOpenCardOptionsMenu';
import { ColorThemeContext, grid } from '@shared/styles';

export interface MBCardMenuProps {
  card: MBAPI.Card;
  menuIsVisible: boolean;
  onMenuClose(): void;
}

export function MBCardMenu({ card, menuIsVisible, onMenuClose }: MBCardMenuProps) {
  const anchorRef = useRef<MBView>(null);
  const anchorName = `--card-menu-${card.id}`;
  const colors = useContext(ColorThemeContext);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const onOpen = useCallback(() => setDropdownOpen(true), []);
  const onClose = useCallback(() => {
    setDropdownOpen(false);
    onMenuClose();
  }, [onMenuClose]);
  const openCardOptionsMenu = useOpenCardOptionsMenu(card, {
    onOpen,
    onClose,
    onShare: () => setShowShareSheet(true),
  });
  const handlePress = useCallback(
    () => anchorRef.current && openCardOptionsMenu(anchorRef.current),
    [openCardOptionsMenu, anchorRef],
  );

  return (
    <MBView
      ref={anchorRef}
      style={[
        styles.cardMenuWrapper,
        { borderRadius: grid(1), position: 'absolute', top: 0, right: 0 },
      ]}
    >
      <View style={{ anchorName }}>
        <MBHover onHoverStart={() => setIsHovered(true)} onHoverEnd={() => setIsHovered(false)}>
          <>
            {(menuIsVisible || dropdownOpen) && (
              <MBIconButton
                source={DotsIcon}
                size="small"
                inverted
                onPress={handlePress}
                backgroundColor={isHovered ? colors.highlightActive : colors.highlightInactive}
              />
            )}
          </>
        </MBHover>
      </View>
      {showShareSheet && (
        <ShareSheet
          anchorName={anchorName}
          card={card}
          onCloseSheet={() => setShowShareSheet(false)}
        />
      )}
    </MBView>
  );
}

const styles = StyleSheet.create({
  cardMenuWrapper: {
    padding: grid(1),
    paddingLeft: 0,
  },
});
